import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { UserPlus, Pencil, Trash2, Users, ShieldCheck } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { useSession } from '../../session/SessionProvider';
import {
  PageHeader, Button, DataTable, Badge, Avatar, Modal, ConfirmDialog, Field, Input, Select, Switch, EmptyState,
} from '../../ui';

import { errorText } from '../../lib/errors';

const ROLES = ['org_admin', 'org_viewer'];

/** Кабинет компании: команда своей компании */
export default function TeamPage() {
  const { company } = useSession();
  return <TeamPanel tenantId={company?.id} />;
}

/** Команда компании tenantId (кабинет компании и карточка компании в консоли владельца) */
export function TeamPanel({ tenantId, embedded = false }) {
  const { t, fmt } = useI18n();
  const { admin, canEdit } = useSession();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const base = `/admin/tenants/${tenantId}/admins`;

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await api.get(base);
      setMembers(res.data.data || []);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setLoading(false);
    }
  }, [base, tenantId, t]);

  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`${base}/${deleting.admin_id}`);
      toast.success(t('team.deleted'));
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const isSelf = (m) => m.admin_id === admin?.admin_id;

  const columns = [
    {
      key: 'username',
      header: t('common.username'),
      sortable: true,
      render: (m) => (
        <div className="flex items-center gap-3">
          <Avatar name={m.username} size="sm" />
          <div className="min-w-0">
            <p className="flex items-center gap-2 font-medium text-zinc-900 dark:text-zinc-100">
              {m.username}
              {isSelf(m) && <Badge tone="brand">{t('common.you')}</Badge>}
            </p>
            <p className="flex items-center gap-1.5 truncate text-xs text-zinc-500">
              {m.email}
              {m.totp_enabled === 1 && <span className="inline-flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400" title={t('twofa.on')}><ShieldCheck className="h-3 w-3" />2FA</span>}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: t('common.role'),
      sortable: true,
      render: (m) => <Badge tone={m.role === 'org_admin' ? 'brand' : 'neutral'}>{t(`roles.${m.role}`)}</Badge>,
    },
    {
      key: 'is_active',
      header: t('common.status'),
      render: (m) => (m.is_active
        ? <Badge tone="success" dot>{t('team.active')}</Badge>
        : <Badge tone="danger" dot>{t('team.disabled')}</Badge>),
    },
    {
      key: 'last_login_at',
      header: t('common.lastLogin'),
      sortable: true,
      hideOnMobile: true,
      render: (m) => <span className="tabular text-zinc-500">{m.last_login_at ? fmt.relative(m.last_login_at) : t('common.never')}</span>,
    },
    {
      key: 'created_at',
      header: t('common.created'),
      sortable: true,
      hideOnMobile: true,
      render: (m) => <span className="tabular text-zinc-500">{fmt.date(m.created_at)}</span>,
    },
  ];

  if (canEdit) {
    columns.push({
      key: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'right',
      render: (m) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon-sm" onClick={() => setEditing(m)} aria-label={`${t('common.edit')} ${m.username}`}>
            <Pencil className="h-4 w-4" />
          </Button>
          {!isSelf(m) && (
            <Button variant="danger-ghost" size="icon-sm" onClick={() => setDeleting(m)} aria-label={`${t('common.delete')} ${m.username}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    });
  }

  return (
    <>
      {embedded ? (
        canEdit && (
          <div className="mb-4 flex justify-end">
            <Button icon={UserPlus} onClick={() => setInviteOpen(true)}>{t('team.invite')}</Button>
          </div>
        )
      ) : (
        <PageHeader
          title={t('team.title')}
          description={t('team.subtitle')}
          actions={canEdit && <Button icon={UserPlus} onClick={() => setInviteOpen(true)}>{t('team.invite')}</Button>}
        />
      )}

      <DataTable
        columns={columns}
        rows={members}
        loading={loading}
        rowKey={(m) => m.admin_id}
        empty={<EmptyState icon={Users} title={t('team.empty')} text={t('team.emptyHint')} />}
      />

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} base={base} onDone={load} />
      <EditModal member={editing} self={editing && isSelf(editing)} onClose={() => setEditing(null)} base={base} onDone={load} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
        loading={busy}
        title={t('team.deleteConfirmTitle')}
        text={t('team.deleteConfirmText', { name: deleting?.username })}
        confirmLabel={t('common.delete')}
      />
    </>
  );
}

function RoleSelect({ value, onChange, disabled }) {
  const { t } = useI18n();
  return (
    <Field label={t('common.role')} hint={t(`roleHints.${value}`)}>
      <Select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        {ROLES.map((r) => <option key={r} value={r}>{t(`roles.${r}`)}</option>)}
      </Select>
    </Field>
  );
}

function InviteModal({ open, onClose, base, onDone }) {
  const { t } = useI18n();
  const empty = { username: '', email: '', password: '', role: 'org_admin' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(empty); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(base, form);
      toast.success(t('team.created'));
      onClose();
      onDone();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('team.inviteTitle')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="invite-form" loading={saving}>{t('common.add')}</Button>
        </>
      }
    >
      <form id="invite-form" onSubmit={submit} className="space-y-4">
        <Field label={t('common.username')} required>
          <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required minLength={2} autoComplete="off" />
        </Field>
        <Field label={t('common.email')} required>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required autoComplete="off" />
        </Field>
        <Field label={t('common.password')} hint={t('team.passwordHint')} required>
          <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} autoComplete="new-password" />
        </Field>
        <RoleSelect value={form.role} onChange={(role) => setForm({ ...form, role })} />
      </form>
    </Modal>
  );
}

function EditModal({ member, self, onClose, base, onDone }) {
  const { t } = useI18n();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (member) setForm({ email: member.email, role: member.role, password: '', is_active: member.is_active, reset_2fa: false });
  }, [member]);

  if (!member || !form) return null;

  const submit = async (e) => {
    e.preventDefault();
    const payload = {};
    if (form.email !== member.email) payload.email = form.email;
    if (form.role !== member.role) payload.role = form.role;
    if (form.is_active !== member.is_active) payload.is_active = form.is_active;
    if (form.password) payload.password = form.password;
    if (form.reset_2fa) payload.reset_2fa = true;
    if (!Object.keys(payload).length) { onClose(); return; }

    setSaving(true);
    try {
      await api.patch(`${base}/${member.admin_id}`, payload);
      toast.success(t('team.updated'));
      onClose();
      onDone();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('team.editTitle')}
      description={member.username}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="edit-member-form" loading={saving}>{t('common.save')}</Button>
        </>
      }
    >
      <form id="edit-member-form" onSubmit={submit} className="space-y-4">
        <Field label={t('common.email')}>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
        </Field>
        <RoleSelect value={form.role} onChange={(role) => setForm({ ...form, role })} disabled={self} />
        <Field label={t('team.newPassword')} hint={t('team.newPasswordHint')}>
          <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} minLength={8} autoComplete="new-password" />
        </Field>
        {!self && member.totp_enabled === 1 && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 px-3 py-2.5 dark:border-zinc-700">
            <span className="text-sm text-zinc-700 dark:text-zinc-300">
              {t('twofa.reset')}
              <span className="block text-xs text-zinc-500">{t('twofa.resetHint')}</span>
            </span>
            <Switch checked={form.reset_2fa} onChange={(v) => setForm({ ...form, reset_2fa: v })} label={t('twofa.reset')} />
          </div>
        )}
        {!self && (
          <div className="flex items-center justify-between rounded-lg border border-zinc-200 px-3 py-2.5 dark:border-zinc-700">
            <span className="text-sm text-zinc-700 dark:text-zinc-300">{form.is_active ? t('team.active') : t('team.disabled')}</span>
            <Switch checked={form.is_active} onChange={(v) => setForm({ ...form, is_active: v })} label={form.is_active ? t('team.disable') : t('team.enable')} />
          </div>
        )}
      </form>
    </Modal>
  );
}
